import React, { useState } from 'react';
import { Link } from 'react-router';
import { Points } from 'components/Points/Points';
import GlassContainer from 'components/GlassContainer/GlassContainer';
import { FoilCardImage } from 'components/FoilCardImage';
import { SetIcon } from 'components/SetIcon';
import { useWindowSize } from 'hooks/useWindowSize';
import './CardTradeTile.css';
import { finishFromRow, HaveFinish } from 'utils/finishes';

// Inline SVG card-back placeholder — never depends on S3/CDN, can't 403.
const CARD_BACK_DATA_URI = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='223' height='311' viewBox='0 0 223 311'%3E%3Crect width='223' height='311' rx='12' fill='%231a1a2e'/%3E%3Crect x='8' y='8' width='207' height='295' rx='8' fill='none' stroke='%236F51E4' stroke-width='2' opacity='0.4'/%3E%3Ctext x='111.5' y='165' text-anchor='middle' fill='%236F51E4' font-size='48' opacity='0.5'%3E%E2%9C%A6%3C/text%3E%3C/svg%3E";

export interface TriggerRect {
  left: number;
  right: number;
  top: number;
}

export interface CardTradeTileCard {
  name: string;
  imageUrl: string | null;
  /** Pre-built href for the card-details link (callers compute via buildCardUrl). */
  href: string;
  setCode?: string | null;
  collectorNumber?: string | null;
  foil?: boolean;
  /** Track D; derived from foil when absent */
  finish?: HaveFinish | null;
}

export interface CardTradeTileProps {
  card: CardTradeTileCard;
  /**
   * Trader identity block (avatar + username + the PERSON's balance), rendered in a row
   * ABOVE the card image. Each page supplies its own (UserIdentityBlock for alerts,
   * the user row for activity). Omit + set `hideTrader` for grouped tiles where the group
   * header already shows the identity once.
   */
  traderSlot?: React.ReactNode;
  hideTrader?: boolean;
  /** The CARD's value, centered BELOW the image. null → render `valueFallback`. */
  cardValue: number | null;
  valueFallback?: React.ReactNode;
  /** Size of the card-value Points component. Defaults to 'big'. */
  valueSize?: 'big' | 'small';
  /** Send affordance. The button only renders when an `onSend` handler is provided. */
  onSend?: () => void;
  sendLabel?: string;
  /** Compact (grouped) variant — drops the fixed image height and tightens spacing. */
  compact?: boolean;
  /** Standalone tiles get the frosted-glass chrome; grouped tiles stay chrome-less. */
  chrome?: 'glass' | 'bare';
  /** Inline style on the tile root (bare chrome only — e.g. activity border/glow color). */
  style?: React.CSSProperties;
  /** Extra class on the tile root (page hook for hover/animation styling). */
  className?: string;
  /** Page chrome positioned over the image (type badge, dismiss button, relative time). */
  imageOverlay?: React.ReactNode;
  /**
   * Hover content rendered inside the image container (desktop only). Receives the trigger
   * rect + a close callback. A WantListCardOverlay positions against the image; a portaled
   * SendCardTooltip can be returned in the same fragment.
   */
  renderHover?: (ctx: { rect: TriggerRect; close: () => void }) => React.ReactNode;
  onImageError?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  onImageClick?: () => void;
  /** Extra class on the image CONTAINER (carries each page's existing image sizing/S-M-L rules). */
  imageContainerClassName?: string;
  /** Extra class on the FoilCardImage element. */
  imageClassName?: string;
  imageBorderRadius?: string;
}

/**
 * Shared card tile used by BOTH the Activity feed and the Trade Alerts page. Owns the
 * redesigned vertical layout — trader identity ABOVE the image, the card's value centered
 * BELOW it (above an optional Send) — plus the desktop image-hover plumbing. Everything
 * page-specific (dismiss/hide, type badge, the flyout content) is injected via slots so
 * this component imports no page data types and the two pages can't drift apart.
 */
export const CardTradeTile: React.FC<CardTradeTileProps> = ({
  card,
  traderSlot,
  hideTrader,
  cardValue,
  valueFallback,
  valueSize = 'big',
  onSend,
  sendLabel = 'Send',
  compact,
  chrome = 'glass',
  style,
  className,
  imageOverlay,
  renderHover,
  onImageError,
  onImageClick,
  imageContainerClassName,
  imageClassName,
  imageBorderRadius = '8px',
}) => {
  const { isMobile } = useWindowSize();
  const [rect, setRect] = useState<TriggerRect | null>(null);
  const showHover = !isMobile && rect !== null;

  const handleImageEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isMobile || !renderHover) return;
    const r = e.currentTarget.getBoundingClientRect();
    setRect({ left: r.left, right: r.right, top: r.top });
  };
  const handleImageLeave = () => setRect(null);

  const metaParts = [card.setCode?.toUpperCase(), card.collectorNumber && `#${card.collectorNumber}`]
    .filter(Boolean)
    .join(' ');
  const setIconUrl = card.setCode
    ? `https://trademagic-assets.s3.us-west-1.amazonaws.com/sets/icons/${card.setCode.toLowerCase()}.svg`
    : null;

  const body = (
    <>
      {!hideTrader && traderSlot && <div className="card-trade-tile__trader">{traderSlot}</div>}

      <div
        className={`card-trade-tile__image ${imageContainerClassName || ''}`}
        onMouseEnter={handleImageEnter}
        onMouseLeave={handleImageLeave}
        onClick={onImageClick}
        role={onImageClick ? 'button' : undefined}
        tabIndex={onImageClick ? 0 : undefined}
        onKeyDown={
          onImageClick
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') onImageClick();
              }
            : undefined
        }
      >
        <FoilCardImage
          src={card.imageUrl || CARD_BACK_DATA_URI}
          alt={card.name}
          finish={finishFromRow(card)}
          onError={onImageError}
          loading="lazy"
          borderRadius={imageBorderRadius}
          className={imageClassName}
        />
        {imageOverlay}
        {showHover && rect && renderHover?.({ rect, close: handleImageLeave })}
      </div>

      {/* Card value — centered directly below the image. */}
      <div className="card-trade-tile__value">
        {cardValue != null ? <Points points={cardValue} type={valueSize} /> : valueFallback}
      </div>

      {/* Card name + metadata */}
      <Link
        className="card-trade-tile__name"
        to={card.href}
        title={card.name}
        onClick={(e) => e.stopPropagation()}
      >
        {card.name}
      </Link>
      {metaParts && (
        <div className="card-trade-tile__meta">
          {setIconUrl && (
            <SetIcon
              setIconUrl={setIconUrl}
              setName={card.setCode || ''}
              size="sm"
              className="card-trade-tile__set-icon"
            />
          )}
          <span>{metaParts}</span>
          {card.foil && <span className="card-trade-tile__foil"> · Foil</span>}
        </div>
      )}

      {onSend && (
        <button className="card-trade-tile__send" onClick={onSend}>
          {sendLabel}
        </button>
      )}
    </>
  );

  const rootClass = ['card-trade-tile', compact && 'card-trade-tile--compact', className]
    .filter(Boolean)
    .join(' ');

  return chrome === 'glass' ? (
    <GlassContainer variant="cardDetails" className={rootClass}>
      {body}
    </GlassContainer>
  ) : (
    <div className={rootClass} style={style}>
      {body}
    </div>
  );
};

export default CardTradeTile;
